import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faMusic, 
  faUser, 
  faPeopleGroup,
  faCalendarDay,
  faBell,
  faEllipsisH
} from "@fortawesome/free-solid-svg-icons";
import "./MobileNavBar.css";

function MobileNavBar() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  // Fetch unread notification count - hooks must be called at the top level
  useEffect(() => {
    // Only fetch if user is authenticated
    if (user) {
      const fetchUnreadCount = async () => {
        try {
          // Import the function dynamically
          const { getUnreadNotificationCount } = await import("../services/notificationService");
          const response = await getUnreadNotificationCount();
          setUnreadCount(response.unread_count);
        } catch (error) {
        }
      };

      fetchUnreadCount();
      
      // Poll for new notifications every 60 seconds
      const intervalId = setInterval(fetchUnreadCount, 60000);
      
      // Set up event listener for notification count updates
      const handleNotificationUpdate = (event) => {
        setUnreadCount(event.detail.unreadCount);
      };
      
      document.addEventListener('notificationCountUpdated', handleNotificationUpdate);
      
      return () => {
        clearInterval(intervalId);
        document.removeEventListener('notificationCountUpdated', handleNotificationUpdate);
      };
    }
  }, [user]);
  
  // Add event listener to close the menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      // Check if the click is outside the navbar and more menu
      if (showMoreMenu && !event.target.closest('.mobile-more-menu') 
          && !event.target.closest('.mobile-navbar-item')) {
        setShowMoreMenu(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMoreMenu]);
  
  // If user is not logged in, don't show the mobile navbar
  if (!user) {
    return null;
  }
  
  // Enhanced isActive function with proper path detection
  const isActive = (path) => {
    const pathname = location.pathname;
    
    // For notifications
    if (path === '/notifications') {
      return pathname === '/notifications';
    }
    
    // For songs
    if (path === '/songs') {
      return pathname.startsWith('/songs');
    }
    
    // For profile
    if (path === '/profile') {
      return pathname.startsWith('/profile');
    }
    
    // For community pages
    if (path === '/community') {
      return pathname.startsWith('/community') || 
             pathname.startsWith('/create-community') || 
             pathname.startsWith('/manage-community') ||
             pathname.startsWith('/invitation') ||
             pathname === '/community/list';
    }
    
    // For events/dashboard (home)
    if (path === '/') {
      // First check if we're on another main section
      if (
        pathname.startsWith('/songs') || 
        pathname.startsWith('/community') ||
        pathname.startsWith('/create-community') ||
        pathname.startsWith('/manage-community') ||
        pathname.startsWith('/profile') ||
        pathname.startsWith('/invitation') ||
        pathname === '/notifications'
      ) {
        return false;
      }
      
      // Otherwise, assume we're on events/dashboard pages
      return true;
    }
    
    // Fallback case - exact path match
    return pathname === path;
  };

  // Handle navigation to events list
  const navigateToEvents = (e) => {
    e.preventDefault();
    
    // If we're on the dashboard with an event parameter, navigate to dashboard without parameters
    // This will show the events list instead of a specific event detail
    if (location.pathname === '/dashboard' && location.search.includes('event=')) {
      navigate('/dashboard', { replace: true });
    } 
    // If we're already on dashboard without parameters or on home, do nothing (already showing list)
    else if (location.pathname === '/dashboard' || location.pathname === '/') {
      // Already on the events list, do nothing
    } 
    // If we're on another page, navigate to dashboard
    else {
      navigate('/dashboard');
    }
  };
  
  // Handle toggle of more menu
  const toggleMoreMenu = (e) => {
    e.preventDefault();
    setShowMoreMenu(!showMoreMenu);
  };

  return (
    <>
      {/* Backdrop for closing the menu when clicking outside */}
      {showMoreMenu && (
        <div className="mobile-menu-backdrop" onClick={() => setShowMoreMenu(false)}></div>
      )}
      
      <div className="mobile-navbar">
        <a href="/"
           onClick={navigateToEvents}
           className={`mobile-navbar-item ${isActive('/') ? 'active' : ''}`}>
          <FontAwesomeIcon icon={faCalendarDay} size="lg" />
          <span>{t('navigation.events')}</span>
        </a>
        <Link to="/songs" className={`mobile-navbar-item ${isActive('/songs') ? 'active' : ''}`}>
          <FontAwesomeIcon icon={faMusic} size="lg" />
          <span>{t('navigation.songbook')}</span>
        </Link>
        <Link to="/notifications" className={`mobile-navbar-item ${isActive('/notifications') ? 'active' : ''}`}>
          <div className="notification-icon-wrapper">
            <FontAwesomeIcon icon={faBell} size="lg" />
            {unreadCount > 0 && (
              <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
            )}
          </div>
          <span>{t('navigation.notifications')}</span>
        </Link>
        <a href="#"
           onClick={toggleMoreMenu}
           className={`mobile-navbar-item ${(isActive('/profile') || isActive('/community')) ? 'active' : ''}`}>
          <FontAwesomeIcon icon={faEllipsisH} size="lg" />
          <span>{t('navigation.more')}</span>
        </a>
        
        {/* More menu portal */}
        {showMoreMenu && (
          <div className="mobile-more-menu">
            <Link
              to="/profile"
              className="more-menu-item"
              onClick={() => setShowMoreMenu(false)}
            >
              <FontAwesomeIcon icon={faUser} className="more-menu-icon" />
              <span>{t('navigation.myProfile')}</span>
            </Link>
            <Link
              to="/community/list"
              className="more-menu-item"
              onClick={() => setShowMoreMenu(false)}
            >
              <FontAwesomeIcon icon={faPeopleGroup} className="more-menu-icon" />
              <span>{t('navigation.communities')}</span>
            </Link>
          </div>
        )}
      </div>
    </>
  );
}

export default MobileNavBar;